import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { ImageManager as CharImageManager } from '@/components/character-form/image-manager';
import { BasicIdentitySection as LocBasicIdentity } from '@/components/location-form/basic-identity';
import { CustomFieldsSection as LocCustomFields } from '@/components/location-form/custom-fields-section';
import { BasicIdentitySection as OrgBasicIdentity } from '@/components/organization-form/basic-identity';
import { CustomFieldsSection as OrgCustomFields } from '@/components/organization-form/custom-fields-section';
import { ImageManager as OrgImageManager } from '@/components/organization-form/image-manager';
import { MembershipSection as OrgMembership } from '@/components/organization-form/membership-section';
import { ImageManager as LocImageManager } from '@/features/locations';
import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import type { Entity, PrimaryEntityType, ProcessedImage } from '@/lib/types';

import { BasicIdentitySection as CharPersona } from '@/components/character-form/basic-identity';
import { CustomFieldsSection as CharCustomFields } from '@/components/character-form/custom-fields-section';

import { EntityVisual } from '@/components/entity-display';
import { useTranslation } from 'react-i18next';

/** Update payload shared by all three ImageManager components. */
export interface ImageManagerUpdate {
  images?: ProcessedImage[];
  pendingFiles?: File[];
  primaryImageId?: string | null;
}

// =============================================================================
// Entity Form Fields
// =============================================================================

interface EntityFormFieldsProps {
  entityType: PrimaryEntityType;
  entityId: string;
  eventId: string;
  novelId: string;
  customSettings: ReturnType<typeof useEntitySettings>['sections'];
  isSaving: boolean;
}

export function EntityFormFields({ entityType, entityId, eventId, novelId, customSettings, isSaving }: EntityFormFieldsProps) {
  const hasCustom = customSettings && customSettings.length > 0;
  switch (entityType) {
    case 'organizations':
      return (
        <>
          <OrgBasicIdentity isNameReadonly />
          {hasCustom && <OrgCustomFields novelId={novelId} sections={customSettings} />}
          <OrgMembership organizationId={entityId} fitViewDependency={eventId} enableRelationshipEditing disableAutoSave isFormSubmitting={isSaving} />
        </>
      );
    case 'locations':
      return (
        <>
          <LocBasicIdentity isNameReadonly />
          {hasCustom && <LocCustomFields novelId={novelId} sections={customSettings} />}
        </>
      );
    case 'characters':
      return (
        <>
          <CharPersona isNameReadonly />
          {hasCustom && <CharCustomFields novelId={novelId} sections={customSettings} />}
        </>
      );
  }
}

// =============================================================================
// Entity Image Section
// =============================================================================

interface EntityImageSectionProps {
  entityType: PrimaryEntityType;
  entityId: string;
  timeTravelState: Entity;
  watchedImages: ProcessedImage[];
  watchedPendingFiles: File[];
  watchedPrimaryImageId: string | null;
  isProcessingImages: boolean;
  watch: (name: string) => unknown;
  onUpdate: (updates: ImageManagerUpdate) => void;
  readOnly?: boolean;
}

export function EntityImageSection({
  entityType, entityId, timeTravelState, watchedImages, watchedPendingFiles,
  watchedPrimaryImageId, isProcessingImages, watch, onUpdate, readOnly
}: EntityImageSectionProps) {
  const { setValue: setFormValue } = useFormContext();
  const { t } = useTranslation();
  const imageEntity = {
    ...timeTravelState,
    id: entityId,
    images: watchedImages,
    primaryImageId: watchedPrimaryImageId,
  };
  const visualDescription = (watch('visualDescription') ?? '') as string;

  const handleVisualDescChange = useCallback((value: string) => {
    setFormValue('visualDescription', value, { shouldDirty: true });
  }, [setFormValue]);

  if (readOnly) {
    return (
      <EntityVisual
        title={t(`entity_view.${entityType}.visual`)}
        images={watchedImages}
        primaryImageId={watchedPrimaryImageId}
        altText={'name' in imageEntity ? String(imageEntity.name) : 'Entity'}
        disableLayout
      />
    );
  }

  switch (entityType) {
    case 'characters':
      return (
        <CharImageManager
          character={imageEntity as Parameters<typeof CharImageManager>[0]['character']}
          pendingFiles={watchedPendingFiles}
          isProcessing={isProcessingImages}
          visualDescription={visualDescription}
          onVisualDescriptionChange={handleVisualDescChange}
          onUpdate={onUpdate}
          disableDeletion={true}
        />
      );
    case 'locations':
      return (
        <LocImageManager
          location={imageEntity as Parameters<typeof LocImageManager>[0]['location']}
          pendingFiles={watchedPendingFiles}
          isProcessing={isProcessingImages}
          entityId={entityId}
          visualDescription={visualDescription}
          onVisualDescriptionChange={handleVisualDescChange}
          onUpdate={onUpdate}
          disableDeletion={true}
        />
      );
    case 'organizations':
      return (
        <OrgImageManager
          organization={imageEntity as Parameters<typeof OrgImageManager>[0]['organization']}
          pendingFiles={watchedPendingFiles}
          isProcessing={isProcessingImages}
          visualDescription={visualDescription}
          onVisualDescriptionChange={handleVisualDescChange}
          onUpdate={onUpdate}
          disableDeletion={true}
        />
      );
  }
}
